import React, { useState } from 'react';
import { TouchableOpacity, View } from 'react-native';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faPlus, faTrashCan } from '@fortawesome/free-solid-svg-icons';
import { translate } from '../../services/translate';
import MyAppText from '../MyAppText';
import MyDataTable from '../MyDataTable';
import DeleteModal from '../DeleteModal';
import InvolvedObjectModal from './InvolvedObjectModal';
import CustomButton from '../CustomButton';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

interface InvolvedObjectsParams {
    involvedObjects: InvolvedObject[];
    setInvolvedObjects: (value: InvolvedObject[]) => void;
    updateOccurrenceInvolved: () => Promise<void>;
}

export default function InvolvedObjects({ involvedObjects, setInvolvedObjects, updateOccurrenceInvolved }: InvolvedObjectsParams) {
    const { styles, theme } = useStyles(styleSheet);

    const [selectedInvolvedObject, setSelectedInvolvedObject] = useState<InvolvedObject>(getBlankObject());
    const [objectModalOpen, setObjectModalOpen] = useState<boolean>(false);
    const [deleteObjectModalVisible, setDeleteObjectModalVisible] = useState(false);

    function getBlankObject(): InvolvedObject {
        return {
            amount: 0,
            objectCategoryId: 0,
            objectSubCategoryId: 0,
            description: ''
        };
    }

    async function createUpdateInvolvedObjects() {
        if (!selectedInvolvedObject.id) {
            selectedInvolvedObject.id = -Math.random();
            involvedObjects.push(selectedInvolvedObject);
        } else {
            const index = involvedObjects.findIndex(object => object.id === selectedInvolvedObject.id);
            involvedObjects[index] = { ...involvedObjects[index], ...selectedInvolvedObject };
        }

        setInvolvedObjects(involvedObjects);
        setSelectedInvolvedObject(getBlankObject());
    }

    return (
        <>
            <View style={{ flex: 1, rowGap: 20 }}>
                <View style={{ flexDirection: 'row', alignItems: 'flex-end', justifyContent: 'space-between' }}>
                    <MyAppText style={styles.headerText}>{translate('involvedObjects')}</MyAppText>
                    <CustomButton
                        onPress={() => setObjectModalOpen(true)}
                        text={translate('attach')}
                        icon={faPlus}
                    />
                </View>
                <MyDataTable
                    columns={[
                        {
                            name: translate('category'),
                            selector: row => row.objectCategory?.name || '',
                            wrap: true,
                        },
                        {
                            name: translate('subCategory'),
                            selector: row => row.objectSubCategory?.name || '',
                            wrap: true,
                        },
                        {
                            name: translate('amount'),
                            selector: row => row.amount || '',
                            wrap: true,
                        },
                        {
                            name: translate('action'),
                            button: true,
                            cell: row =>
                                <View style={{ flexDirection: 'row', columnGap: 10 }}>
                                    <TouchableOpacity onPress={() => {
                                        setSelectedInvolvedObject(row);
                                        setObjectModalOpen(true);
                                    }}>
                                        <FontAwesomeIcon icon={faEdit} fontSize={16} color={theme.colors.iconColor} />
                                    </TouchableOpacity>
                                    <TouchableOpacity onPress={() => {
                                        setSelectedInvolvedObject(row);
                                        setDeleteObjectModalVisible(true);
                                    }}>
                                        <FontAwesomeIcon icon={faTrashCan} fontSize={16} color={theme.colors.iconColor} />
                                    </TouchableOpacity>
                                </View>
                        }
                    ]}
                    data={involvedObjects}
                />
            </View>
            <DeleteModal
                setModalVisible={setDeleteObjectModalVisible}
                isModalVisible={deleteObjectModalVisible}
                itemName={translate('object')}
                onSubmit={() => {
                    const index = involvedObjects.findIndex(object => object.id === selectedInvolvedObject.id);
                    if (index >= 0) {
                        involvedObjects.splice(index, 1);
                        setInvolvedObjects(involvedObjects);
                        setSelectedInvolvedObject(getBlankObject());
                        updateOccurrenceInvolved();
                    }
                }}
                onCancel={() => {
                    setSelectedInvolvedObject(getBlankObject());
                }}
            />
            <InvolvedObjectModal
                involvedObject={selectedInvolvedObject}
                setInvolvedObject={setSelectedInvolvedObject}
                isModalVisible={objectModalOpen}
                setModalVisible={(value) => {
                    if (!value) {
                        setSelectedInvolvedObject(getBlankObject());
                    }
                    setObjectModalOpen(value);
                }}
                onSubmit={async () => {
                    await createUpdateInvolvedObjects();
                    updateOccurrenceInvolved();
                }}
            />
        </>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    headerText: {
        color: theme.colors.labelColor,
        fontSize: 16,
        fontWeight: 'bold',
    }
}));
